import { toast } from '@/lib/hooks/use-toast'
import { NON_QUOTE_EDIT_MUTATION_KEY_ROOT } from '@/lib/quote-activation-gate'
import { trackException } from '@/lib/telemetry'
import { FieldServiceProductType } from '@/lib/types'
import { isCatalogPackage, isProduct, PRODUCT_REQUIREMENT } from '@/lib/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { createRecord, deleteRecord, getMultipleRecords, getRecord, updateRecord } from './helpers'
import { encodeODataLiteral } from './products'
import { QUERY_KEYS } from './query-keys'
import { normalizePackageName, type QuoteDetailData } from './quote-details'
import { useQuoteQuery } from './quotes'

// The reserved header product's unit schedule and unit, reused verbatim: a
// catalog package is quantity-1 "Each" like the ad-hoc header it replaces.
const UOM_SCHEDULE_ID = 'e5e5ee04-4452-40d0-8d0b-5449776106c9'
const UOM_EACH_ID = 'c778fe16-8f7b-45a4-944b-aaa82e570ad6'

/**
 * Catalog-package writes touch the product catalog, never the quote, so they
 * carry NON_QUOTE_EDIT_MUTATION_KEY_ROOT and bypass the quote-editable gate —
 * exactly like templates.
 */
export const CATALOG_PACKAGE_MUTATION_KEYS = {
  save: [NON_QUOTE_EDIT_MUTATION_KEY_ROOT, 'save-catalog-package'],
  update: [NON_QUOTE_EDIT_MUTATION_KEY_ROOT, 'update-catalog-package'],
  deactivate: [NON_QUOTE_EDIT_MUTATION_KEY_ROOT, 'deactivate-catalog-package'],
} as const

// ==================== Contents ====================

/** One row of a package's contents: a product and how many of it the package holds. */
export type PackageContent = {
  productId: string
  /** Shown in the save dialog so the author can see what is being captured. */
  name: string
  quantity: number
  requirement: number
  /**
   * The captured row's own parent member on the source quote, written literally
   * to dsy_packageparentproduct. Null is a top-level member. The insert nests
   * from this, so the tree the author saved is the tree that comes back.
   */
  parentProductId: string | null
}

/**
 * Package contents from the rows a package was built out of.
 *
 * Members AND the accessories hanging under them are captured, so an insert
 * puts back everything the author saved, in the shape it was saved in: each
 * content row records its own parent member, so nothing is inferred from the
 * catalog's accessory definitions on the way back in.
 *
 * Headers, scope and summary rows are not products. KNOWN LIMIT: rows are keyed
 * by product, so a product appearing in two positions saves as a single member
 * row — the same product captured twice collapses into one row with the
 * quantities summed, and one captured in two different positions (member and
 * accessory, or under two different members) is PROMOTED to a member. Every
 * read the insert makes is product-keyed — the parent resolution, the item
 * build and the slot map — so two rows sharing a product would come back as two
 * copies of whichever position won, which is a duplicated line and a missing
 * one. A member is the position that survives insertion unconditionally.
 *
 * An accessory whose own parent is not itself being captured has nothing to
 * nest under and is captured as a top-level member.
 */
export function buildPackageContents(rows: QuoteDetailData[]): PackageContent[] {
  const productIdByGlobalId = new Map<string, string>()
  for (const row of rows) {
    const globalId = row.dsy_cpqfields.dsy_globalid
    if (globalId && !row.dsy_cpqfields.dsy_parentglobalid && isProduct(row.productid)) {
      productIdByGlobalId.set(globalId, row.productid.productid)
    }
  }

  const byProduct = new Map<string, PackageContent>()

  for (const row of rows) {
    if (!isProduct(row.productid)) continue

    // An accessory whose parent is not itself being captured has nothing to nest
    // under, so it is captured as a member rather than silently dropped.
    const parentGlobalId = row.dsy_cpqfields.dsy_parentglobalid
    const parentProductId = parentGlobalId ? (productIdByGlobalId.get(parentGlobalId) ?? null) : null

    const productId = row.productid.productid
    const quantity = Math.max(1, Math.round(row.quantity || 1))
    const existing = byProduct.get(productId)
    if (existing) {
      existing.quantity += quantity
      // Two positions for one product: neither can be written literally, so the
      // row becomes a member — the one position an insert always reproduces.
      if (!parentProductId || existing.parentProductId !== parentProductId) existing.parentProductId = null
      continue
    }
    byProduct.set(productId, {
      productId,
      name: row.productid.name,
      quantity,
      requirement: PRODUCT_REQUIREMENT.Required,
      parentProductId,
    })
  }

  return Array.from(byProduct.values())
}

/**
 * The dsy_productaccessory row one content is written as.
 *
 * dsy_parentproduct is the PACKAGE the row belongs to; dsy_PackageParentProduct
 * is the content's own parent member inside it, omitted for a top-level member.
 * The navigation property is the lookup's schema name, cased exactly as the
 * column was created (see dataverse-scripts setup/add_package_parent_product_column.py).
 */
function packageContentBody(packageProductId: string, content: PackageContent) {
  return {
    'dsy_parentproduct@odata.bind': `/products(${packageProductId})`,
    'dsy_accessoryproduct@odata.bind': `/products(${content.productId})`,
    ...(content.parentProductId
      ? { 'dsy_PackageParentProduct@odata.bind': `/products(${content.parentProductId})` }
      : {}),
    dsy_quantity: content.quantity,
    dsy_requirement: content.requirement,
  }
}

// ==================== Uniqueness ====================

const packageNameConflictRowSchema = z.object({
  productid: z.string(),
  name: z.string().nullable(),
  productnumber: z.string().nullable(),
  dsy_ispackage: z.boolean().nullish(),
  // $top=1 on the expand: presence, not a count — one row is enough to know the
  // package is still offered somewhere.
  dsy_pricelistitem_product_product: z.array(z.object({ dsy_pricelistitemid: z.string() })).nullish(),
})

export type PackageNameConflict = {
  productid: string
  name: string | null
  productnumber: string | null
  dsy_ispackage?: boolean | null
  /**
   * A package with no price list item anywhere is deactivated: nothing can find
   * it, but it still owns its stamped name.
   */
  hasPriceListItem: boolean
}

/**
 * Catalog rows already using a package name, checked across the WHOLE product
 * catalog rather than only packages.
 *
 * Deliberately NOT the create-product "reuse the existing product" path: the
 * server stamps a package's name and product number from dsy_model, so a name
 * collision with an ordinary product would silently make two catalog rows
 * indistinguishable in search. A collision is rejected and the author renames.
 */
async function findPackageNameConflicts(name: string): Promise<PackageNameConflict[]> {
  const normalized = normalizePackageName(name)
  if (!normalized) return []

  const encoded = encodeODataLiteral(normalized)
  const rows = await getMultipleRecords<unknown>(
    'products',
    `?$select=productid,name,productnumber,dsy_ispackage` +
      `&$expand=dsy_pricelistitem_product_product($select=dsy_pricelistitemid;$top=1)` +
      `&$filter=name eq '${encoded}' or productnumber eq '${encoded}'&$top=5`
  )
  return rows.map((row) => {
    const parsed = packageNameConflictRowSchema.parse(row)
    return {
      productid: parsed.productid,
      name: parsed.name,
      productnumber: parsed.productnumber,
      dsy_ispackage: parsed.dsy_ispackage,
      hasPriceListItem: (parsed.dsy_pricelistitem_product_product ?? []).length > 0,
    }
  })
}

/** The message shown when a name is already taken, naming what took it. */
export function getPackageNameConflictMessage(conflicts: PackageNameConflict[]): string | null {
  if (!conflicts.length) return null
  const conflict = conflicts[0]
  const label = conflict.name ?? conflict.productnumber

  // A deactivated package is invisible everywhere — search, the manage dialog,
  // the catalog — so "already a catalog package" would name something the
  // author cannot go find. Reactivation is v2; today the only move is a rename.
  if (isCatalogPackage(conflict) && !conflict.hasPriceListItem) {
    return `"${label}" is a deactivated catalog package that still holds this name. Pick a different name.`
  }

  const what = isCatalogPackage(conflict) ? 'package' : 'product'
  return `"${label}" is already a catalog ${what}. Pick a different name.`
}

/**
 * The one place a catalog name is checked: the conflict read and the message it
 * produces. Null means the name is free.
 *
 * `ignoreProductId` is the package being renamed — its own row is not a
 * collision with itself.
 */
export async function checkCatalogName(
  name: string,
  { ignoreProductId }: { ignoreProductId?: string } = {}
): Promise<string | null> {
  const conflicts = (await findPackageNameConflicts(name)).filter(
    (conflict) => conflict.productid !== ignoreProductId
  )
  return getPackageNameConflictMessage(conflicts)
}

// ==================== Save ====================

export type SaveCatalogPackageVariables = {
  name: string
  description: string
  /** Optional catalog image, already validated by the dialog. */
  image?: File | null
  contents: PackageContent[]
}

/** Base64 payload Dataverse expects for entityimage, or undefined for no image. */
export async function readImageAsBase64(file: File | null | undefined): Promise<string | undefined> {
  if (!file) return undefined
  return await new Promise<string | undefined>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result?.toString().split(',')[1])
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/**
 * Save a package to the catalog: a product record, its contents, and a price
 * list item so it is searchable at this branch.
 *
 * The product carries no manufacturer — StampProductIdentity derives name and
 * product number from dsy_model server-side (uppercased), which is why only the
 * model is written. dsy_requiredonpricelist stays false: distribution beyond the
 * saving branch is an admin decision, not a side effect of saving.
 *
 * The price list item is created last and priced at zero. A package header row
 * is always $0 — the package's price is the sum of the members materialized
 * under it, at whatever those members cost on the quote's price list today.
 *
 * The zeroed pricing and labor on the PRODUCT are load-bearing, not tidiness: a
 * price-list-item create stamps its rates from the product and ignores the ones
 * the request sends, so a product left with null labor produces a price list
 * item with null rates — which priceListItemSchema rejects, silently dropping
 * the package from every product search.
 *
 * Everything after the product create is rolled back on failure. A package
 * whose contents or price list item never landed is an Active product with no
 * price list item: invisible in search, invisible in the manage dialog, and
 * squatting the stamped name forever — the author's retry would collide with
 * the wreckage of their own first attempt.
 */
export async function saveCatalogPackage({
  name,
  description,
  image,
  contents,
  priceListId,
}: SaveCatalogPackageVariables & { priceListId: string }) {
  const normalizedName = normalizePackageName(name)
  if (!normalizedName) throw new Error('Package name is required')
  if (!contents.length) throw new Error('A catalog package needs at least one item')

  const conflictMessage = await checkCatalogName(normalizedName)
  if (conflictMessage) throw new Error(conflictMessage)

  const productId = await createRecord(
    'products',
    {
      entityimage: await readImageAsBase64(image),
      dsy_model: normalizedName,
      description,
      dsy_ispackage: true,
      dsy_requiredonpricelist: false,
      // A package is a quote-side construct: headers are excluded from sales
      // order publication, so a NetSuite item for it would be a $0 phantom.
      // Without this the column default (true) applies.
      dsy_syncwithnetsuite: false,
      msdyn_fieldserviceproducttype: FieldServiceProductType.OtherCharge,
      'defaultuomscheduleid@odata.bind': `/uomschedules(${UOM_SCHEDULE_ID})`,
      'defaultuomid@odata.bind': `/uoms(${UOM_EACH_ID})`,
      price: 0,
      currentcost: 0,
      standardcost: 0,
      msdyn_taxable: false,
      dsy_installationlabor: 0,
      dsy_programminglabor: 0,
      dsy_roughinlabor: 0,
      dsy_engineeringlabor: 0,
      dsy_projectmanagementlabor: 0,
    },
    '',
    true
  )

  // Ids are collected as each create resolves, not from the settled Promise.all
  // — a rejected batch still leaves the ones that already landed behind.
  const createdContentIds: string[] = []

  try {
    await Promise.all(
      contents.map(async (content) => {
        const contentId = await createRecord(
          'dsy_productaccessories',
          packageContentBody(productId, content),
          '',
          true
        )
        createdContentIds.push(contentId)
      })
    )

    const priceListItemId = await createRecord(
      'dsy_pricelistitems',
      {
        'dsy_product@odata.bind': `/products(${productId})`,
        'dsy_pricelist@odata.bind': `/pricelevels(${priceListId})`,
        dsy_description: description,
        dsy_unitcost: 0,
        dsy_unitprice: 0,
        dsy_taxable: false,
        dsy_engineeringlabor: 0,
        dsy_installationlabor: 0,
        dsy_roughinlabor: 0,
        dsy_programminglabor: 0,
        dsy_projectmanagementlabor: 0,
      },
      '',
      true
    )

    return { productId, priceListItemId }
  } catch (error) {
    await rollbackPartialPackage({ productId, contentIds: createdContentIds })
    throw error
  }
}

/**
 * Undo a half-written package, contents first so the product delete has nothing
 * referencing it.
 *
 * A rollback failure is reported and swallowed: the caller is already throwing
 * the reason the save failed, and that message is the useful one.
 */
async function rollbackPartialPackage({ productId, contentIds }: { productId: string; contentIds: string[] }) {
  try {
    await Promise.all(contentIds.map((contentId) => deleteRecord('dsy_productaccessories', contentId, true)))
    await deleteRecord('products', productId, true)
  } catch (rollbackError) {
    trackException(rollbackError instanceof Error ? rollbackError : new Error(String(rollbackError)), {
      context: 'catalog-packages.rollbackPartialPackage',
      productId,
      contentIds: contentIds.join(','),
    })
  }
}

export function useSaveCatalogPackage() {
  const queryClient = useQueryClient()
  const { data: quote } = useQuoteQuery()

  return useMutation({
    mutationKey: [...CATALOG_PACKAGE_MUTATION_KEYS.save],
    mutationFn: async (variables: SaveCatalogPackageVariables) => {
      const priceListId = quote?._pricelevelid_value
      if (!priceListId) throw new Error('Price list not found on quote')
      return await saveCatalogPackage({ ...variables, priceListId })
    },
    onSuccess: () => {
      toast({ title: 'Package saved to the catalog', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Error saving package', error, variant: 'destructive' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.catalogPackagesRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.productsRoot })
    },
  })
}

// ==================== List / edit / deactivate ====================

const catalogPackageSchema = z.object({
  dsy_pricelistitemid: z.string(),
  dsy_product: z.object({
    productid: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    entityimage_url: z.string().nullish(),
    dsy_ispackage: z.boolean().nullish(),
    dsy_daisymanaged: z.boolean().nullish(),
  }),
})

export type CatalogPackageData = z.infer<typeof catalogPackageSchema>

/**
 * Catalog packages visible on this quote's price list.
 *
 * Branch scoping is structural: a package is only searchable where a price list
 * item exists for it, so listing by the quote's price list IS the branch filter.
 * State is never filtered — removing the price list item is what hides a
 * package, so a listed row is by definition still offered.
 */
export function useCatalogPackagesQuery() {
  const { data: quote } = useQuoteQuery()
  const priceListId = quote?._pricelevelid_value

  return useQuery({
    queryKey: QUERY_KEYS.catalogPackages(priceListId),
    enabled: !!priceListId,
    staleTime: 30_000,
    queryFn: async () => {
      if (!priceListId) throw new Error('Price list not found on quote')
      const rows = await getMultipleRecords<unknown>(
        'dsy_pricelistitems',
        `?$select=dsy_pricelistitemid` +
          `&$expand=dsy_product($select=productid,name,description,entityimage_url,dsy_ispackage,dsy_daisymanaged)` +
          `&$filter=_dsy_pricelist_value eq '${encodeODataLiteral(priceListId)}' and dsy_product/dsy_ispackage eq true` +
          `&$orderby=dsy_name asc`
      )
      return rows
        .map((row) => catalogPackageSchema.parse(row))
        .filter((row) => isCatalogPackage(row.dsy_product))
        // Sorted on the field the list actually renders. The server orders by
        // the price-list item's own name, which the manage dialog never shows,
        // so at catalog scale the ordering reads as random.
        .sort((a, b) => a.dsy_product.name.localeCompare(b.dsy_product.name))
    },
  })
}

/** A package nobody at this branch may edit: the shared, distributed ones. */
export function isCatalogPackageLocked(catalogPackage: CatalogPackageData) {
  return catalogPackage.dsy_product.dsy_daisymanaged === true
}

export type UpdateCatalogPackageVariables = {
  productId: string
  /** New name, already trimmed by the caller. Omitted leaves the name alone. */
  name?: string
  description?: string
  image?: File | null
}

export function useUpdateCatalogPackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [...CATALOG_PACKAGE_MUTATION_KEYS.update],
    mutationFn: async ({ productId, name, description, image }: UpdateCatalogPackageVariables) => {
      // Re-read rather than trust the list: an edit must never land on an
      // ordinary product or on a package this branch does not own.
      const product = await getRecord<{ dsy_ispackage?: boolean | null; dsy_daisymanaged?: boolean | null }>(
        'products',
        productId,
        '?$select=productid,dsy_ispackage,dsy_daisymanaged'
      )
      if (product.dsy_ispackage !== true) throw new Error('That catalog row is not a package.')
      if (product.dsy_daisymanaged === true) throw new Error('This package is managed centrally and cannot be edited.')

      const body: Record<string, unknown> = {}
      if (name !== undefined) {
        const normalizedName = normalizePackageName(name)
        if (!normalizedName) throw new Error('Package name is required')
        const conflictMessage = await checkCatalogName(normalizedName, { ignoreProductId: productId })
        if (conflictMessage) throw new Error(conflictMessage)
        // Name and product number are stamped from the model server-side.
        body.dsy_model = normalizedName
      }
      if (description !== undefined) body.description = description
      if (image) body.entityimage = await readImageAsBase64(image)

      if (!Object.keys(body).length) return
      await updateRecord('products', productId, body, true)
    },
    onSuccess: () => {
      toast({ title: 'Package updated', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Error updating package', error, variant: 'destructive' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.catalogPackagesRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.productsRoot })
    },
  })
}

export type UpdateCatalogPackageContentsVariables = {
  productId: string
  /** The header's transitive member set — members and their accessories. */
  rows: QuoteDetailData[]
}

export type UpdateCatalogPackageContentsResult = {
  written: number
  failed: number
  /** Old content rows that could not be deleted and are still in the package. */
  leftover: number
}

/**
 * Rewrite a catalog package's contents from a package on a quote.
 *
 * The product record, its name, its image and its price list item are all left
 * alone: only the dsy_productaccessory rows are replaced. That is what makes
 * this the answer to "my package has the wrong quantity" — a re-save under the
 * same name collides with the package's own stamped name, and the only escape
 * was a near-duplicate catalog row.
 *
 * Delete-then-create rather than a diff: dsy_productaccessory holds no identity
 * beyond (parent, content, content's parent), so a rewrite is the same number of
 * writes and has no partial-diff state to reason about.
 *
 * Nothing here is allowed to leave the catalog package EMPTY — it is shared by
 * every quote at the branch and has no rollback. So the deletes are settled, not
 * awaited as a batch: a rejected delete leaves a stale row behind, which is a
 * duplicate at worst, while letting it reject the rewrite would leave a package
 * whose other contents were already deleted and never recreated. The creates
 * settle for the same reason. Only a total failure of either wave throws.
 */
export async function updateCatalogPackageContents({
  productId,
  rows,
}: UpdateCatalogPackageContentsVariables): Promise<UpdateCatalogPackageContentsResult> {
  const contents = buildPackageContents(rows)
  if (!contents.length) throw new Error('A catalog package needs at least one item')

  // Same re-read as every other catalog write: a rewrite must never land on
  // an ordinary product or on a package this branch does not own.
  const product = await getRecord<{ dsy_ispackage?: boolean | null; dsy_daisymanaged?: boolean | null }>(
    'products',
    productId,
    '?$select=productid,dsy_ispackage,dsy_daisymanaged'
  )
  if (product.dsy_ispackage !== true) throw new Error('That catalog row is not a package.')
  if (product.dsy_daisymanaged === true) throw new Error('This package is managed centrally and cannot be edited.')

  const existing = await getMultipleRecords<{ dsy_productaccessoryid: string }>(
    'dsy_productaccessories',
    `?$select=dsy_productaccessoryid&$filter=_dsy_parentproduct_value eq '${encodeODataLiteral(productId)}'`
  )
  const settledDeletes = await Promise.allSettled(
    existing.map((row) => deleteRecord('dsy_productaccessories', row.dsy_productaccessoryid, true))
  )
  const leftover = settledDeletes.filter((result) => result.status === 'rejected').length
  // Every delete failing means the write has no reach at all — the package is
  // untouched, so say so rather than doubling its contents.
  if (existing.length && leftover === existing.length) {
    throw (settledDeletes.find((result) => result.status === 'rejected') as PromiseRejectedResult).reason
  }

  const settled = await Promise.allSettled(
    contents.map((content) => createRecord('dsy_productaccessories', packageContentBody(productId, content), '', true))
  )

  const failures = settled.filter((result) => result.status === 'rejected')
  if (failures.length === contents.length) throw failures[0].reason
  return { written: contents.length - failures.length, failed: failures.length, leftover }
}

export function useUpdateCatalogPackageContents() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [...CATALOG_PACKAGE_MUTATION_KEYS.update, 'contents'],
    mutationFn: async (variables: UpdateCatalogPackageContentsVariables) =>
      await updateCatalogPackageContents(variables),
    onSuccess: (result) => {
      if (result.failed || result.leftover) {
        const notes = [`Saved ${result.written} of ${result.written + result.failed} items.`]
        if (result.leftover) notes.push(`${result.leftover} old item(s) could not be removed and are still in it.`)
        toast({
          title: 'Package contents updated without every item',
          description: notes.join(' '),
          variant: 'destructive',
        })
        return
      }
      toast({ title: 'Package contents updated', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Error updating package contents', error, variant: 'destructive' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.packageContentPriceListItemsRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.catalogPackagesRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.productsRoot })
    },
  })
}

/**
 * Take a package out of search by deleting its price list item(s) on one price
 * list. Product search has no state filter — price list item presence IS the
 * visibility switch — so removing the row is the only thing that hides it, and
 * quotes that already hold the package keep their rows untouched.
 */
export async function deactivateCatalogPackage({
  productId,
  priceListId,
}: {
  productId: string
  priceListId: string
}) {
  const priceListItems = await getMultipleRecords<{ dsy_pricelistitemid: string }>(
    'dsy_pricelistitems',
    `?$select=dsy_pricelistitemid&$filter=_dsy_product_value eq '${encodeODataLiteral(productId)}'` +
      ` and _dsy_pricelist_value eq '${encodeODataLiteral(priceListId)}'`
  )
  await Promise.all(
    priceListItems.map((item) => deleteRecord('dsy_pricelistitems', item.dsy_pricelistitemid, true))
  )
  return priceListItems.length
}

export function useDeactivateCatalogPackage() {
  const queryClient = useQueryClient()
  const { data: quote } = useQuoteQuery()

  return useMutation({
    mutationKey: [...CATALOG_PACKAGE_MUTATION_KEYS.deactivate],
    mutationFn: async (productId: string) => {
      const priceListId = quote?._pricelevelid_value
      if (!priceListId) throw new Error('Price list not found on quote')

      // Same re-read as the edit path: hiding a row must never land on an
      // ordinary product or on a package this branch does not own.
      const product = await getRecord<{ dsy_ispackage?: boolean | null; dsy_daisymanaged?: boolean | null }>(
        'products',
        productId,
        '?$select=productid,dsy_ispackage,dsy_daisymanaged'
      )
      if (product.dsy_ispackage !== true) throw new Error('That catalog row is not a package.')
      if (product.dsy_daisymanaged === true) throw new Error('This package is managed centrally and cannot be removed.')

      return await deactivateCatalogPackage({ productId, priceListId })
    },
    onSuccess: () => {
      toast({ title: 'Package removed from search' })
    },
    onError: (error) => {
      toast({ title: 'Error removing package', error, variant: 'destructive' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.catalogPackagesRoot })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.productsRoot })
    },
  })
}
